import { useState } from "react";
import { Link } from "react-router-dom";
import CourseOverview from "./CourseOverview";

function TopicWiseCourse({ course, topics, testSeries }) {
  const [openTopics, setOpenTopics] = useState(() => new Set());
  const [openSeries, setOpenSeries] = useState(() => new Set());
  const [seriesMenuOpen, setSeriesMenuOpen] = useState(true);
  const [selected, setSelected] = useState(null);

  const toggleTopic = (topic) => {
    setOpenTopics((current) => {
      const next = new Set(current);
      if (next.has(topic.id)) next.delete(topic.id);
      else next.add(topic.id);
      return next;
    });
    setSelected({ kind: "topic", topic });
  };

  const toggleSeries = (series) => {
    setOpenSeries((current) => {
      const next = new Set(current);
      if (next.has(series.id)) next.delete(series.id);
      else next.add(series.id);
      return next;
    });
    setSelected({ kind: "series", series });
  };

  return (
    <div className="learn-shell">
      <aside className="learn-menu" aria-label="Course menu">
        <h2 className="learn-menu-title">Topics</h2>
        <div className="learn-menu-scroll">
        {topics.length === 0 ? <p className="learn-note">No topics yet.</p> : null}
        {topics.map((topic) => {
          const open = openTopics.has(topic.id);
          return (
            <div key={topic.id}>
              <button
                type="button"
                className={`learn-topic${open ? " is-open" : ""}`}
                aria-expanded={open}
                onClick={() => toggleTopic(topic)}
              >
                <span>{topic.name}</span>
                <span aria-hidden="true">{open ? "-" : "+"}</span>
              </button>
              {open ? (
                <div className="learn-sections">
                  {topic.videosAvailable ? (
                    <>
                      <p className="learn-section-label">Videos</p>
                      <p className="learn-note">
                        {topic.videoCount > 0
                          ? `${topic.videoCount} video classes`
                          : "No videos yet"}
                      </p>
                    </>
                  ) : null}
                  <p className="learn-section-label">Tests</p>
                  {topic.tests.length === 0 ? (
                    <p className="learn-note">No tests yet</p>
                  ) : (
                    topic.tests.map((test) => (
                      <button
                        key={test.id}
                        type="button"
                        className={`learn-item${
                          selected?.kind === "test" && selected.test.id === test.id
                            ? " is-active"
                            : ""
                        }`}
                        onClick={() => setSelected({ kind: "test", topic, test })}
                      >
                        {test.title}
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          );
        })}

        {course.fullTestsAvailable ? (
          <div className="learn-series-block">
            <button
              type="button"
              className={`learn-topic learn-series-head${seriesMenuOpen ? " is-open" : ""}`}
              aria-expanded={seriesMenuOpen}
              onClick={() => setSeriesMenuOpen((open) => !open)}
            >
              <span>Full length test series</span>
              <span aria-hidden="true">{seriesMenuOpen ? "-" : "+"}</span>
            </button>
            {seriesMenuOpen ? (
              testSeries.length === 0 ? (
                <p className="learn-note">No test series yet</p>
              ) : (
                testSeries.map((series) => {
                  const open = openSeries.has(series.id);
                  return (
                    <div key={series.id}>
                      <button
                        type="button"
                        className={`learn-series${open ? " is-open" : ""}`}
                        aria-expanded={open}
                        onClick={() => toggleSeries(series)}
                      >
                        <span>{series.title}</span>
                        <span aria-hidden="true">{open ? "-" : "+"}</span>
                      </button>
                      {open ? (
                        series.tests.length === 0 ? (
                          <p className="learn-note">No active tests</p>
                        ) : (
                          series.tests.map((test) => (
                            <button
                              key={test.id}
                              type="button"
                              className={`learn-item${
                                selected?.kind === "seriesTest" && selected.test.id === test.id
                                  ? " is-active"
                                  : ""
                              }`}
                              onClick={() => setSelected({ kind: "seriesTest", series, test })}
                            >
                              {test.title}
                            </button>
                          ))
                        )
                      ) : null}
                    </div>
                  );
                })
              )
            ) : null}
          </div>
        ) : null}
        </div>
      </aside>

      <section className="learn-content" aria-live="polite">
        {!selected ? (
          <CourseOverview course={course} topics={topics} testSeries={testSeries} />
        ) : null}

        {selected?.kind === "topic" ? (
          <>
            <h2>{selected.topic.name}</h2>
            {selected.topic.description ? <p>{selected.topic.description}</p> : null}
            {selected.topic.videosAvailable ? (
              <p>
                {selected.topic.videoCount > 0
                  ? `${selected.topic.videoCount} video classes`
                  : "No videos in this topic yet."}
              </p>
            ) : null}
            <p>
              {selected.topic.tests.length === 1
                ? "1 test"
                : `${selected.topic.tests.length} tests`}
            </p>
          </>
        ) : null}

        {selected?.kind === "test" ? (
          <>
            <p className="learn-kicker">{selected.topic.name}</p>
            <h2>{selected.test.title}</h2>
            <dl className="learn-facts">
              <div>
                <dt>Questions</dt>
                <dd>{selected.test.totalQuestions}</dd>
              </div>
              <div>
                <dt>Marks per question</dt>
                <dd>{selected.test.marksPerQuestion}</dd>
              </div>
              <div>
                <dt>Negative mark</dt>
                <dd>{selected.test.negativeMark}</dd>
              </div>
              {selected.test.attempt ? (
                <div>
                  <dt>Last score</dt>
                  <dd>{selected.test.attempt.marks}</dd>
                </div>
              ) : null}
            </dl>
            <div className="test-actions">
              <Link className="btn-view-course" to={`/my-courses/${course.id}/tests/topic/${selected.test.id}`}>
                {selected.test.attempt ? "Re-attempt" : "Attempt test"}
              </Link>
              {selected.test.attempt ? (
                <Link
                  className="test-action-secondary"
                  to={`/my-courses/${course.id}/tests/topic/${selected.test.id}/result`}
                >
                  Result
                </Link>
              ) : null}
              {selected.test.attempt ? (
                <Link
                  className="test-action-secondary"
                  to={`/my-courses/${course.id}/tests/topic/${selected.test.id}/analysis`}
                >
                  Analysis
                </Link>
              ) : null}
            </div>
          </>
        ) : null}

        {selected?.kind === "series" ? (
          <>
            <p className="learn-kicker">
              {selected.series.examType
                ? `${selected.series.examType} test series`
                : "Test series"}
            </p>
            <h2>{selected.series.title}</h2>
            {selected.series.description ? <p>{selected.series.description}</p> : null}
            <p>
              {selected.series.tests.length === 1
                ? "1 test"
                : `${selected.series.tests.length} tests`}
            </p>
          </>
        ) : null}

        {selected?.kind === "seriesTest" ? (
          <>
            <p className="learn-kicker">{selected.series.title}</p>
            <h2>{selected.test.title}</h2>
            {selected.test.description ? <p>{selected.test.description}</p> : null}
            <dl className="learn-facts">
              <div>
                <dt>Questions</dt>
                <dd>{selected.test.totalQuestions}</dd>
              </div>
              <div>
                <dt>Marks per question</dt>
                <dd>{selected.test.marksPerQuestion}</dd>
              </div>
              <div>
                <dt>Negative mark</dt>
                <dd>{selected.test.negativeMark}</dd>
              </div>
              {selected.test.duration > 0 ? (
                <div>
                  <dt>Duration</dt>
                  <dd>{selected.test.duration} min</dd>
                </div>
              ) : null}
              {selected.test.attempt ? (
                <div>
                  <dt>Last score</dt>
                  <dd>{selected.test.attempt.marks}</dd>
                </div>
              ) : null}
            </dl>
            <div className="test-actions">
              <Link className="btn-view-course" to={`/my-courses/${course.id}/tests/series/${selected.test.id}`}>
                {selected.test.attempt ? "Re-attempt" : "Attempt test"}
              </Link>
              {selected.test.attempt ? (
                <Link
                  className="test-action-secondary"
                  to={`/my-courses/${course.id}/tests/series/${selected.test.id}/analysis`}
                >
                  Analysis
                </Link>
              ) : null}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}

export default TopicWiseCourse;
